import {create} from 'zustand'
import {persist} from 'zustand/middleware'
import {IUserPreferences} from '../types/auth'

type SidebarView = 'legacy' | 'new'

export type Lang = 'en' | 'es'

/**
 * Idioma de la PRIMERA visita, deducido del navegador.
 *
 * El middleware `persist` rehidrata desde localStorage cuando ya hay un valor
 * guardado, asi que esto solo decide la primera vez y nunca pisa una eleccion
 * explicita del usuario. Sin esto, un agente hispanohablante que abre la app en
 * un equipo nuevo aterriza en ingles antes de poder iniciar sesion.
 */
const detectBrowserLang = (): Lang => {
  if (typeof navigator === 'undefined') return 'en'
  const preferred = navigator.languages?.[0] || navigator.language || ''
  return preferred.toLowerCase().startsWith('es') ? 'es' : 'en'
}

type State = {
  sidebarView: SidebarView
  sidebarFavorites: string[]
  expandedSections: string[]
  language: Lang
}

type Actions = {
  setSidebarView: (view: SidebarView) => void
  setFavorites: (favorites: string[]) => void
  toggleFavorite: (route: string) => void
  toggleSection: (sectionId: string) => void
  setLanguage: (lang: Lang) => void
  hydrate: (prefs: IUserPreferences) => void
  reset: () => void
}

export const useUserPreferencesStore = create<State & Actions>()(
  persist(
    (set) => ({
      sidebarView: 'new',
      sidebarFavorites: [],
      expandedSections: [],
      language: detectBrowserLang(),
      setSidebarView: (view) => set({sidebarView: view}),
      setLanguage: (lang) => set({language: lang}),
      setFavorites: (favorites) => set({sidebarFavorites: favorites}),
      toggleFavorite: (route) =>
        set((state) => ({
          sidebarFavorites: state.sidebarFavorites.includes(route)
            ? state.sidebarFavorites.filter((r) => r !== route)
            : [...state.sidebarFavorites, route],
        })),
      toggleSection: (sectionId) =>
        set((state) => ({
          expandedSections: state.expandedSections.includes(sectionId)
            ? state.expandedSections.filter((s) => s !== sectionId)
            : [...state.expandedSections, sectionId],
        })),
      hydrate: (prefs) =>
        set((state) => ({
          sidebarView: prefs.sidebar_view ?? 'new',
          sidebarFavorites: prefs.sidebar_favorites ?? [],
          // Manda lo que el usuario tenga guardado en SU cuenta: el idioma le sigue
          // entre dispositivos y un puesto compartido no contagia el del anterior.
          // El selector del login solo sirve para poder leer esa pantalla.
          // Keep the current language if the backend has none saved yet.
          language: prefs.language ?? state.language,
        })),
      // Note: language is intentionally NOT reset on logout — it stays as a
      // device-level fallback until the next user's saved language hydrates.
      reset: () => set({sidebarView: 'new', sidebarFavorites: [], expandedSections: []}),
    }),
    {name: 'user-preferences'}
  )
)
